import { Button } from '@packages/ui/core';
import '@packages/ui/tailwind.css';
import {
  isRouteErrorResponse,
  json,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigation,
  useRouteError,
} from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { LoaderFunctionArgs } from '@vercel/remix';
import { clsx } from 'clsx';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import { HiExclamationTriangle } from 'react-icons/hi2';
import { getToast } from 'remix-toast';
import { ClientOnly } from 'remix-utils/client-only';
import { toast as notify, Toaster } from 'sonner';
import packageJson from '../package.json';
import { syncMetaCookies } from './.client/device-info';
import RouteTracker from './.client/route-tracker';
import { UserSession } from './.server/user-session';
import { FirebaseManager } from './firebase-manager';
import './styles.css';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { user, otherUsers } = await UserSession.getUsers(request);

  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  }

  const { toast, headers } = await getToast(request);

  return json(
    {
      user: user?.toJSON(),
      otherUsers,
      toast,
      ENV: {
        APP_ENV: process.env.APP_ENV,
        APP_VERSION: packageJson.version ?? 'Unknown',
        GOOGLE_MAPS_API_KEY: process.env.GOOGLE_MAPS_API_KEY,
        FIREBASE_API_KEY: process.env.FIREBASE_API_KEY,
        FIREBASE_AUTH_DOMAIN: process.env.FIREBASE_AUTH_DOMAIN,
        FIREBASE_PROJECT_ID: process.env.FIREBASE_PROJECT_ID,
        FIREBASE_STORAGE_BUCKET: process.env.FIREBASE_STORAGE_BUCKET,
        FIREBASE_MESSAGING_SENDER_ID: process.env.FIREBASE_MESSAGING_SENDER_ID,
        FIREBASE_APP_ID: process.env.FIREBASE_APP_ID,
        FIREBASE_MEASUREMENT_ID: process.env.FIREBASE_MEASUREMENT_ID,
        FIREBASE_REGION: process.env.FIREBASE_REGION,
        FIREBASE_VAPID_KEY: process.env.FIREBASE_VAPID_KEY,
        STREAM_CHAT_KEY: process.env.STREAM_CHAT_KEY,
        WITH_FIREBASE_EMULATORS: process.env.WITH_FIREBASE_EMULATORS,
        WITH_DOCKER: process.env.WITH_DOCKER,
        MIXPANEL_TOKEN: process.env.MIXPANEL_TOKEN,
      },
    },
    { headers },
  );
};

export default function App() {
  const loaderData = useLoaderData<typeof loader>();

  // Sync device ID and timezone to cookies for server-side meta attribution
  useEffect(() => {
    syncMetaCookies();
  }, []);

  useEffect(() => {
    if (loaderData.toast?.type === 'error') {
      notify.error(loaderData.toast.message);
    }

    if (loaderData.toast?.type === 'success') {
      notify.success(loaderData.toast.message);
    }
  }, [loaderData.toast]);

  useEffect(() => {
    if (!loaderData.user) {
      return;
    }

    FirebaseManager.initializeApps([loaderData.user.email, ...loaderData.otherUsers.map(otherUser => otherUser.email)]);
  }, []);

  useEffect(() => {
    if (loaderData.user) {
      Sentry.setUser({ id: loaderData.user.id, email: loaderData.user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [loaderData.user]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <GlobalLoading />

        <Outlet />

        <script
          dangerouslySetInnerHTML={{
            __html: `window.env = ${JSON.stringify(loaderData.ENV)}`,
          }}
        />

        <ScrollRestoration />
        <Scripts />
        <Toaster position="top-right" expand richColors />

        <ClientOnly>{() => <RouteTracker userId={loaderData.user?.id ?? null} />}</ClientOnly>
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const isNetworkError = error instanceof Error && error.message.includes('Failed to fetch');

  const title = isNetworkError ? 'Connection lost' : isRouteErrorResponse(error) ? `Error ${error.status}` : 'Something went wrong';

  const description = isNetworkError
    ? 'Please check your internet connection and try again.'
    : 'An unexpected error occurred. Please try again.';

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <div className="flex min-h-screen items-center justify-center">
          <div className="flex flex-col items-center space-y-4 text-center">
            <div className="bg-destructive/10 text-destructive rounded-full p-4">
              <HiExclamationTriangle className="h-8 w-8" />
            </div>
            <h1 className="text-foreground text-xl font-semibold">{title}</h1>
            <p className="text-muted-foreground max-w-sm text-sm">{description}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </div>

        <ScrollRestoration />
        <Scripts />
        <Toaster position="top-right" expand richColors />
      </body>
    </html>
  );
}

const GlobalLoading = () => {
  const { state } = useNavigation();

  const active = state !== 'idle';
  const [animationComplete, setAnimationComplete] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    if (active) {
      setAnimationComplete(false);
    }

    Promise.allSettled(ref.current.getAnimations().map(({ finished }) => finished)).then(() => !active && setAnimationComplete(true));
  }, [active]);

  return (
    <div
      role="progressbar"
      aria-hidden={!active}
      aria-valuetext={active ? 'Loading' : undefined}
      className="fixed inset-x-0 left-0 top-0 z-[9999] h-1"
    >
      <div
        ref={ref}
        className={clsx(
          state === 'idle' && animationComplete && 'w-0 opacity-0 transition-none',
          state === 'submitting' && 'w-4/12',
          state === 'loading' && 'w-10/12',
          state === 'idle' && !animationComplete && 'w-full',
          'bg-primary h-full transition-all duration-500 ease-in-out',
        )}
      />
    </div>
  );
};
